import { getActivePlatformAdapter } from './platform.js';
import type { NotifySlot } from '@petrhythm/core';

type ApiError = { code?: string; message?: string };
export type ShareFeedItem = { id: string; ciphertext: string; createdAt: number; actorLabel: string };
export type ShareFeedResponse = { items: ShareFeedItem[]; cursor: number; hasMore: boolean; etag?: string; notModified?: boolean };
export type ShareMember = { labelToken: string; acceptedAt: number };
export type CareFeedItem = { id: string; ciphertext: string; createdAt: number; actorLabel: string; actorRole: 'guardian' | 'provider' };
export type CareFeedResponse = { items: CareFeedItem[]; cursor: number; hasMore: boolean; etag?: string; notModified?: boolean };
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '');
const TOKEN_KEY = 'petrhythm.subject_token';

export function remoteConfigured(): boolean {
  return Boolean(API_BASE);
}

export async function ensureSubjectToken(): Promise<string | null> {
  if (!API_BASE) return null;
  const epoch = localStorage.getItem('petrhythm.purge_epoch');
  const assertCurrentDevice = () => { if (localStorage.getItem('petrhythm.purge_epoch') !== epoch) throw new Error('기기 연결이 바뀌었어요. 다시 시도해 주세요.'); };
  const current = localStorage.getItem(TOKEN_KEY);
  if (current) {
    try {
      const encoded = current.split('.')[1]!.replaceAll('-', '+').replaceAll('_', '/');
      const payload = JSON.parse(atob(encoded + '='.repeat((4 - (encoded.length % 4)) % 4))) as { exp?: number };
      if (typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() + 7 * 86_400_000) return current;
    } catch {
      // malformed or stale token: request a fresh install-scoped token below
    }
    localStorage.removeItem(TOKEN_KEY);
  }
  const adapter = getActivePlatformAdapter();
  const installId = await adapter.auth.getSubject();
  assertCurrentDevice();
  const session = adapter.auth.kind === 'toss' ? { channel: 'ait_anonymous', installId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Seoul' } : { channel: 'web', installId, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Seoul' };
  const response = await fetch(`${API_BASE}/v1/session`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(session) });
  if (!response.ok) throw new Error('서버에 연결하지 못했지만 기록은 기기에 저장돼 있어요.');
  const body = await response.json() as { subjectToken?: string };
  if (!body.subjectToken) throw new Error('서버에 연결하지 못했지만 기록은 기기에 저장돼 있어요.');
  // A late response must not recreate a connection removed by device purge.
  assertCurrentDevice();
  localStorage.setItem(TOKEN_KEY, body.subjectToken);
  return body.subjectToken;
}

async function request<T>(path: string, init: RequestInit): Promise<T | null> {
  if (!API_BASE) return null;
  const token = await ensureSubjectToken();
  if (!token) return null;
  const response = await fetch(`${API_BASE}${path}`, { ...init, headers: { 'content-type': 'application/json', authorization: `Bearer ${token}`, ...(init.headers ?? {}) } });
  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiError | null;
    throw new Error(error?.message ?? '연결할 수 없어요.');
  }
  return response.json() as Promise<T>;
}

async function authorizedRequest(path: string, init: RequestInit = {}): Promise<Response | null> {
  if (!API_BASE) return null;
  const token = await ensureSubjectToken();
  if (!token) return null;
  return fetch(`${API_BASE}${path}`, { ...init, headers: { authorization: `Bearer ${token}`, ...(init.headers ?? {}) } });
}

export function createShareInvite(petId: string): Promise<{ inviteId: string; token: string; expiresAt: number } | null> {
  return request('/v1/share/invite', { method: 'POST', body: JSON.stringify({ petId }) });
}

export function createCareInvite(petId: string, episodeId: string, expiresAt: number): Promise<{ inviteId: string; token: string; expiresAt: number } | null> {
  return request('/v1/care/invite', { method: 'POST', body: JSON.stringify({ petId, episodeId, expiresAt }) });
}

export function acceptCareInvite(token: string, labelToken: string): Promise<{ ok: boolean; petId: string; episodeId: string } | null> {
  return request('/v1/care/accept', { method: 'POST', body: JSON.stringify({ token, labelToken }) });
}

export async function fetchCareFeed(episodeId: string, after = 0, etag?: string): Promise<CareFeedResponse | null> {
  const response = await authorizedRequest(`/v1/care/feed?episodeId=${encodeURIComponent(episodeId)}&after=${after}`, etag ? { headers: { 'If-None-Match': etag } } : undefined);
  if (!response) return null;
  if (response.status === 304) return { items: [], cursor: after, hasMore: false, etag, notModified: true };
  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiError | null;
    throw new Error(error?.message ?? '연결할 수 없어요.');
  }
  const body = await response.json() as Omit<CareFeedResponse, 'etag'>;
  return { ...body, etag: response.headers.get('etag') ?? undefined };
}

export async function pushCareFeed(episodeId: string, ciphertext: string): Promise<{ cursor: number; count: number } | null> {
  const response = await authorizedRequest('/v1/care/feed', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ episodeId, ciphertext }) });
  if (!response) return null;
  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiError | null;
    throw new Error(error?.message ?? '연결할 수 없어요.');
  }
  return response.json() as Promise<{ cursor: number; count: number }>;
}

export function acceptShareInvite(token: string, labelToken: string): Promise<{ ok: boolean; petId: string } | null> {
  return request('/v1/share/accept', { method: 'POST', body: JSON.stringify({ token, labelToken }) });
}

export async function fetchShareMembers(petId: string): Promise<ShareMember[] | null> {
  const response = await authorizedRequest(`/v1/share/members?petId=${encodeURIComponent(petId)}`);
  if (!response) return null;
  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiError | null;
    throw new Error(error?.message ?? '연결할 수 없어요.');
  }
  const body = await response.json() as { members?: ShareMember[] };
  return body.members ?? [];
}

export async function fetchShareFeed(petId: string, after = 0, etag?: string): Promise<ShareFeedResponse | null> {
  const response = await authorizedRequest(`/v1/share/feed?petId=${encodeURIComponent(petId)}&after=${after}`, etag ? { headers: { 'If-None-Match': etag } } : undefined);
  if (!response) return null;
  if (response.status === 304) return { items: [], cursor: after, hasMore: false, etag, notModified: true };
  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiError | null;
    throw new Error(error?.message ?? '연결할 수 없어요.');
  }
  const body = await response.json() as Omit<ShareFeedResponse, 'etag'>;
  return { ...body, etag: response.headers.get('etag') ?? undefined };
}

export async function pushShareFeed(petId: string, ciphertext: string): Promise<{ cursor: number; count: number } | null> {
  const response = await authorizedRequest('/v1/share/feed', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ petId, ciphertext }) });
  if (!response) return null;
  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiError | null;
    throw new Error(error?.message ?? '연결할 수 없어요.');
  }
  return response.json() as Promise<{ cursor: number; count: number }>;
}

export async function revokeShareInvite(inviteId: string): Promise<boolean> {
  const response = await authorizedRequest(`/v1/share/invite/${encodeURIComponent(inviteId)}`, { method: 'DELETE' });
  if (!response) return false;
  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiError | null;
    throw new Error(error?.message ?? '연결할 수 없어요.');
  }
  return true;
}

/** Upload only caller-encrypted bytes. Health records must never be passed here directly. */
export async function putRemoteBackup(bytes: Uint8Array, etag?: string): Promise<string | null> {
  const response = await authorizedRequest('/v1/backup', {
    method: 'PUT',
    headers: { 'content-type': 'application/octet-stream', ...(etag ? { 'if-match': etag } : { 'if-none-match': '*' }) },
    body: bytes.buffer as ArrayBuffer,
  });
  if (!response) return null;
  if (!response.ok) throw new Error(response.status === 412 ? '다른 기기의 백업이 먼저 바뀌었어요. 다시 불러온 뒤 시도해 주세요.' : '서버에 연결하지 못했지만 기록은 기기에 저장돼 있어요.');
  return response.headers.get('etag');
}

export async function getRemoteBackup(): Promise<{ bytes: Uint8Array; etag?: string } | null> {
  const response = await authorizedRequest('/v1/backup');
  if (!response) return null;
  if (!response.ok) throw new Error(response.status === 404 ? '보관된 클라우드 백업이 없어요.' : '서버에 연결하지 못했지만 기록은 기기에 저장돼 있어요.');
  return { bytes: new Uint8Array(await response.arrayBuffer()), etag: response.headers.get('etag') ?? undefined };
}

export async function deleteRemoteBackup(): Promise<boolean | null> {
  const response = await authorizedRequest('/v1/backup', { method: 'DELETE' });
  if (!response) return null;
  if (!response.ok) throw new Error('서버에 연결하지 못했지만 기록은 기기에 저장돼 있어요.');
  return true;
}

export async function syncNotifySlots(slots: NotifySlot[], timezone: string): Promise<boolean | null> {
  const response = await authorizedRequest('/v1/notify/slots', { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ slots, timezone }) });
  if (!response) return null;
  if (!response.ok) {
    const error = await response.json().catch(() => null) as ApiError | null;
    throw new Error(error?.message ?? '연결할 수 없어요.');
  }
  return true;
}

export async function acknowledgeNotifySlot(slotKey:string,date:string):Promise<boolean>{const r=await authorizedRequest('/v1/notify/ack',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({slotKey,date})});return Boolean(r?.ok);}
export async function testNotification():Promise<boolean>{const r=await authorizedRequest('/v1/notify/test',{method:'POST'});return Boolean(r?.ok);}
